import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

export const QrScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
    const [cameraError, setCameraError] = useState(null);
    const scannerRef = useRef(null);
    const isStoppingRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let html5QrCode = null;
        const elementId = "qr-reader-element";
        isStoppingRef.current = false;

        const startScanner = async () => {
            try {
                setCameraError(null);
                html5QrCode = new Html5Qrcode(elementId);
                scannerRef.current = html5QrCode;

                const config = {
                    fps: 10,
                    qrbox: (viewfinderWidth, viewfinderHeight) => {
                        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
                        const edgeSize = Math.floor(minEdge * 0.7);
                        return { width: edgeSize, height: edgeSize };
                    },
                    aspectRatio: 1.0,
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        if (isStoppingRef.current) return;
                        isStoppingRef.current = true;

                        if (scannerRef.current && scannerRef.current.isScanning) {
                            scannerRef.current
                                .stop()
                                .then(() => {
                                    scannerRef.current = null;
                                    onScanSuccess(decodedText);
                                })
                                .catch(() => {
                                    scannerRef.current = null;
                                    onScanSuccess(decodedText);
                                });
                        } else {
                            onScanSuccess(decodedText);
                        }
                    },
                    () => {
                        // ignore per-frame parse failures
                    }
                );
            } catch (err) {
                console.error("Camera scan error:", err);
                let message = "Kamera başlatılamadı. Lütfen kamera izinlerinizi kontrol ediniz.";
                if (err && typeof err === "string") {
                    message = err;
                } else if (err?.message) {
                    message = err.message;
                }
                setCameraError(message);
            }
        };

        const timer = setTimeout(() => {
            startScanner();
        }, 200);

        return () => {
            clearTimeout(timer);
            if (scannerRef.current) {
                try {
                    if (scannerRef.current.isScanning) {
                        scannerRef.current.stop().catch(() => {});
                    }
                } catch (e) {
                    // ignore
                }
                scannerRef.current = null;
            }
        };
    }, [isOpen]);

    const handleClose = () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            scannerRef.current
                .stop()
                .catch(() => {})
                .finally(() => {
                    scannerRef.current = null;
                    onClose();
                });
        } else {
            scannerRef.current = null;
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">QR / Barkod Tara</h3>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-4 flex flex-col items-center">
                    {cameraError ? (
                        <div className="text-center py-6 px-2">
                            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto flex items-center justify-center mb-3">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-sm text-red-600 font-medium mb-4">{cameraError}</p>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-300"
                            >
                                Kapat
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="w-full relative rounded-xl overflow-hidden bg-black aspect-square flex items-center justify-center">
                                <div id="qr-reader-element" className="w-full h-full" />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                                Kamerayı QR koda veya barkoda doğru tutunuz.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
